/** useDraftVersions — fetch the current draft (inactive) config and prompt versions.
 *
 * Uses the agentConfig and agentPrompt entity APIs (already wired to shared/api).
 *
 * Deviation: this hook uses a direct API call instead of the SettingsRuntime
 * injection abstraction. It should be refactored to use SettingsRuntime when
 * the FSD settings container layer is completed (follow-on task).
 */

import { useCallback, useEffect, useState } from 'react';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import { createAgentConfigApi } from '@/entities/agentConfig';
import { createAgentPromptApi } from '@/entities/agentPrompt';
import type { AgentConfigVersionView } from '@/entities/agentConfig';
import type { AgentPromptVersionView } from '@/entities/agentPrompt';

// Re-export types that callers depend on.
export type { AgentConfigVersionView as ConfigVersion, AgentPromptVersionView as PromptVersion };

function getApiClient() {
  return createApiClient({
    baseUrl: readEnv('VITE_API_BASE_URL') ?? '',
    auth: { getToken },
  });
}

export interface DraftVersionsState {
  activeConfig: AgentConfigVersionView | null;
  activePrompt: AgentPromptVersionView | null;
  draftConfig: AgentConfigVersionView | null;
  draftPrompt: AgentPromptVersionView | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  publishDrafts: () => Promise<void>;
  discardDrafts: () => Promise<void>;
}

export function useDraftVersions(agentId: string): DraftVersionsState {
  const [activeConfig, setActiveConfig] = useState<AgentConfigVersionView | null>(null);
  const [activePrompt, setActivePrompt] = useState<AgentPromptVersionView | null>(null);
  const [draftConfig, setDraftConfig] = useState<AgentConfigVersionView | null>(null);
  const [draftPrompt, setDraftPrompt] = useState<AgentPromptVersionView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const client = getApiClient();
      const configApi = createAgentConfigApi(client);
      const promptApi = createAgentPromptApi(client);
      const [activeCfg, activePmt, cfg, pmt] = await Promise.all([
        configApi.getActive(agentId),
        promptApi.getActive(agentId),
        configApi.getDraft(agentId),
        promptApi.getDraft(agentId),
      ]);
      setActiveConfig(activeCfg);
      setActivePrompt(activePmt);
      setDraftConfig(cfg);
      setDraftPrompt(pmt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { void reload(); }, [reload]);

  const publishDrafts = useCallback(async () => {
    const client = getApiClient();
    const configApi = createAgentConfigApi(client);
    const promptApi = createAgentPromptApi(client);
    if (draftConfig) await configApi.activate(agentId, draftConfig.id);
    if (draftPrompt) await promptApi.activate(agentId, draftPrompt.id);
    await reload();
  }, [agentId, draftConfig, draftPrompt, reload]);

  const discardDrafts = useCallback(async () => {
    const client = getApiClient();
    const configApi = createAgentConfigApi(client);
    const promptApi = createAgentPromptApi(client);
    if (draftConfig) await configApi.deleteVersion(agentId, draftConfig.id);
    if (draftPrompt) await promptApi.deleteVersion(agentId, draftPrompt.id);
    await reload();
  }, [agentId, draftConfig, draftPrompt, reload]);

  return {
    activeConfig,
    activePrompt,
    draftConfig,
    draftPrompt,
    loading,
    error,
    reload,
    publishDrafts,
    discardDrafts,
  };
}
